// src/components/kuestion/student/StudentDashboard.jsx
//
// What a student sees: a sitting in progress to resume (backend response A3:
// status, session_id, answered_count and server-authoritative
// seconds_remaining), the proctored exam waiting for them, practice papers,
// and their own module strengths — from self-assessment attempts only (§7).
// Every deadline names the zone (B3).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useStudentAuth } from '../../../context/StudentAuthContext';
import { useStudentApi } from '../useKuestionApi';
import { Loading, Alert, RuleHead, ModuleRow, Note, Dialog } from '../shared/Bits';
import { WarnIcon } from '../shared/Icons';
import { normaliseAssignments, isDone, spokenDuration } from './assignmentShape';
import StudentTopbar from './StudentTopbar';
import { isOpenNow, isProctored, relativeWindow, parseDate, fmtClock, fmtDate } from '../shared/format';
import { kq } from '../../../config/routes';

export default function StudentDashboard() {
  const navigate = useNavigate();
  const api = useStudentApi();
  const { student } = useStudentAuth();
  const [assignments, setAssignments] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    api.get(API_ENDPOINTS.CAMPUS_MY_ASSIGNMENTS)
      .then((d) => { if (live) setAssignments(normaliseAssignments(d)); })
      .catch((e) => { if (live) { setError(e.message); setAssignments([]); } });
    api.get(API_ENDPOINTS.CAMPUS_ANALYTICS_ME)
      .then((d) => { if (live) setAnalytics(d || { modules: [] }); })
      .catch(() => { if (live) setAnalytics({ modules: [] }); });
    return () => { live = false; };
  }, [api]);

  const { inProgress, waiting, practice, upcoming } = useMemo(() => {
    const all = assignments || [];
    const done = isDone;
    const prog = all.filter((a) => a.status === 'in_progress');
    const proctored = all.filter((a) => isProctored(a.mode));
    const w = proctored.filter((a) => !done(a) && a.status !== 'in_progress' && isOpenNow(a.opens_at, a.closes_at))
      .sort((a, b) => (parseDate(a.closes_at) || Infinity) - (parseDate(b.closes_at) || Infinity));
    const up = proctored.filter((a) => !done(a) && !isOpenNow(a.opens_at, a.closes_at) && parseDate(a.opens_at) && parseDate(a.opens_at) > new Date());
    const p = all.filter((a) => !isProctored(a.mode) && a.status !== 'in_progress');
    return { inProgress: prog, waiting: w, practice: p, upcoming: up };
  }, [assignments]);

  const modules = analytics?.modules || [];
  const history = analytics?.history || [];
  const min = analytics?.min_questions_for_strength;
  const bestFor = (a) => {
    const rows = history.filter((h) => (h.assignment_id != null && String(h.assignment_id) === String(a.id)) || h.template_name === a.template_name);
    if (!rows.length) return null;
    return { attempts: rows.length, best: Math.max(...rows.map((h) => Number(h.percent ?? h.percentage ?? 0))) };
  };

  const [shortWarn, setShortWarn] = useState(null);
  const open = (a) => navigate(kq(`student/exam/${a.id}`), { state: { assignment: a } });
  // Starting is what spends the time, so the warning comes before the tap.
  // A sitting already in progress has nothing left to warn about.
  const start = (a) => (a.short_window && a.status !== 'in_progress' ? setShortWarn(a) : open(a));

  return (
    <div className="kqc">
      <StudentTopbar />
      <main className="kqc-page--center">
        <h1 className="kqc-h1--big" style={{ marginBottom: 12 }}>Hello, {student?.first_name || 'there'}</h1>
        <p className="kqc-lede kqc-lede--big" style={{ marginBottom: 'clamp(28px,4vw,44px)', maxWidth: '48ch', fontSize: 'clamp(15px,1.6vw,18px)' }}>
          {assignments === null ? 'Loading your exams…'
            : inProgress.length ? 'You have a sitting in progress. Pick it up where you stopped — the clock has been running.'
              : waiting.length ? `You have ${waiting.length === 1 ? 'one exam' : `${waiting.length} exams`} waiting. Everything else here is practice — take it as many times as you like.`
                : 'Nothing is waiting for you right now. Everything below is practice — take it as many times as you like.'}
        </p>

        {error && <Alert kind="error">{error}</Alert>}
        {shortWarn && (
          <Dialog title="Less time than the paper allows" danger icon={<WarnIcon />} onClose={() => setShortWarn(null)}>
            <h3>{shortWarn.template_name} closes in {spokenDuration(shortWarn.seconds_available)}</h3>
            <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 16 }}>
              This paper allows {spokenDuration(shortWarn.time_limit_seconds)}, but the exam closes before that.
              If you start now you get {spokenDuration(shortWarn.seconds_available)} and no more.
              {shortWarn.mode && String(shortWarn.mode).startsWith('proctored') ? ' You have one attempt, and starting uses it.' : ''}
            </p>
            <div className="kqc-row">
              <button type="button" className="btn btn-secondary" onClick={() => setShortWarn(null)}>Not now</button>
              <button type="button" className="btn btn-primary" onClick={() => { const a = shortWarn; setShortWarn(null); open(a); }}>Start with {spokenDuration(shortWarn.seconds_available)}</button>
            </div>
          </Dialog>
        )}
        {assignments === null && <Loading label="Loading your exams" />}

        {inProgress.map((a) => (
          <div className="kqc-waiting" key={a.id}>
            <div className="kqc-waiting__head kqc-waiting__head--soft">
              <span>In progress</span>
              <span className="kqc-spacer" />
              {a.seconds_remaining != null && <span>{fmtClock(a.seconds_remaining)} still on the clock</span>}
            </div>
            <div className="kqc-waiting__body">
              <div>
                <h2>{a.template_name || 'Exam'}</h2>
                <div className="kqc-row" style={{ gap: 8, marginBottom: 14 }}>
                  <span className={`tag ${isProctored(a.mode) ? 'tag-accent' : 'tag-neutral'}`}>{isProctored(a.mode) ? 'Proctored' : 'Practice'}</span>
                  {a.answered_count != null && <span className="tag tag-neutral">{a.answered_count} answered</span>}
                </div>
                <Note>You left this sitting {a.answered_count != null ? `after ${a.answered_count} answer${a.answered_count === 1 ? '' : 's'}` : 'part-way through'}. The clock kept running while you were away; if it runs out the exam is submitted with whatever you have answered.</Note>
              </div>
              <button type="button" className="btn btn-primary btn-lg" style={{ flex: '0 0 auto' }} onClick={() => start(a)}>Resume the exam</button>
            </div>
          </div>
        ))}

        {waiting.map((a) => (
          <div className="kqc-waiting" key={a.id}>
            <div className="kqc-waiting__head">
              <span>Waiting for you</span>
              <span className="kqc-spacer" />
              <span>{relativeWindow(a.opens_at, a.closes_at)}</span>
            </div>
            <div className="kqc-waiting__body">
              <div>
                <h2>{a.template_name || 'Exam'}</h2>
                <div className="kqc-row" style={{ gap: 8, marginBottom: 14 }}>
                  <span className="tag tag-accent">Counts towards your grade</span>
                  {a.question_count != null && <span className="tag tag-neutral">{a.question_count} questions</span>}
                  {a.time_limit != null && <span className="tag tag-neutral">{a.time_limit} minutes</span>}
                </div>
                <Note>You get <strong>one attempt</strong>. Once you submit you cannot sit it again, and your teacher &mdash; not this screen &mdash; releases the result.</Note>
              </div>
              <button type="button" className="btn btn-primary btn-lg" style={{ flex: '0 0 auto' }} onClick={() => start(a)}>Start the exam</button>
            </div>
          </div>
        ))}

        {upcoming.map((a) => (
          <div className="kqc-waiting" key={a.id} style={{ borderColor: 'var(--color-divider)' }}>
            <div className="kqc-waiting__head kqc-waiting__head--soft">
              <span>Scheduled</span>
              <span className="kqc-spacer" />
              <span>{relativeWindow(a.opens_at, a.closes_at)}</span>
            </div>
            <div className="kqc-waiting__body">
              <div>
                <h2>{a.template_name || 'Exam'}</h2>
                <span className="kqc-small kqc-muted">Proctored &middot; one attempt. It opens at the time above, shown in your zone.</span>
              </div>
            </div>
          </div>
        ))}

        <div className="kqc-cols" style={{ gap: 'clamp(22px,3vw,40px)' }}>
          <div className="is-main" style={{ flexBasis: 300 }}>
            <RuleHead title="Where you stand" />
            <p className="kqc-small kqc-muted" style={{ marginTop: -8, marginBottom: 16 }}>From your practice attempts only.</p>
            {analytics === null ? <Loading label="Loading strengths" /> : modules.length === 0 ? (
              <div className="kqc-empty">No practice attempts yet. Sit a practice paper and your module strengths appear here.</div>
            ) : (
              <div className="kqc-list">
                {modules.map((m) => (
                  <ModuleRow key={m.module} m={m} min={min} stacked hint={m.sufficient === false ? `Only ${m.attempted || 0} question${m.attempted === 1 ? '' : 's'} so far — try ${Math.max(1, (min || 0) - (m.attempted || 0))} more for a score` : undefined} />
                ))}
              </div>
            )}
            {history.length > 0 && (
              <div style={{ marginTop: 22 }}>
                <RuleHead title="Your attempts" as="h3" />
                <div className="kqc-list kqc-list--soft">
                  {history.slice(0, 5).map((h, i) => (
                    <div key={h.session_id ?? i} className="kqc-list__row is-static kqc-sitrow">
                      <span className="kqc-sitrow__name">{h.template_name}</span>
                      <span className="kqc-small kqc-muted">{fmtDate(h.completed_at)}</span>
                      {h.passed != null && <span className={`kqc-verdict is-${h.passed ? 'pass' : 'fail'}`} title={h.pass_percentage != null ? `${h.pass_percentage}% to pass` : undefined}>{h.passed ? 'Pass' : 'Fail'}</span>}
                      <span className={`kqc-sitrow__score${h.passed === false ? ' is-low' : ''}`}>{Math.round(Number(h.percent ?? h.percentage ?? 0))}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="is-side" style={{ maxWidth: 400, flexBasis: 280 }}>
            <RuleHead title="Practice freely" />
            {assignments !== null && practice.length === 0 ? (
              <div className="kqc-empty">No practice papers assigned yet.</div>
            ) : (
              <div className="kqc-practice">
                {practice.map((a) => {
                  const b = bestFor(a);
                  const open = isOpenNow(a.opens_at, a.closes_at);
                  return (
                    <button type="button" key={a.id} onClick={() => start(a)} disabled={!open}>
                      <span>
                        <strong>{a.template_name || 'Practice'}</strong>
                        <em>
                          {!open ? relativeWindow(a.opens_at, a.closes_at)
                            : a.best_percentage != null ? `Best so far ${Math.round(a.best_percentage)}% · ${a.attempts || 0} attempt${a.attempts === 1 ? '' : 's'}`
                              : b ? `Best so far ${Math.round(b.best)}% · ${b.attempts} attempt${b.attempts === 1 ? '' : 's'}`
                                : 'Never attempted'}
                        </em>
                      </span>
                      <span className="cta">Start &rarr;</span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="kqc-small kqc-muted" style={{ marginTop: 14 }}>Practice results are yours. Your teacher can see them, but no other student can.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
